/**
 * Settings Presets Store - Pinia
 * 
 * Manages activity-specific geolocation presets.
 * Each activity type can have multiple presets with different
 * geolocation configurations.
 * 
 * Features:
 * - Multiple presets per activity type
 * - Universal presets (activityTypeId = null)
 * - One active preset at a time
 * - Default preset per activity
 * - Cascade delete when activity type is deleted
 */

import { defineStore } from 'pinia';
import { appDebugLog } from '@/utils/appDebugLog';
import GeoMoverDB from '@/db/geoMoverDB';

export const useSettingsPresetsStore = defineStore('settingsPresets', {
  state: () => ({
    // All presets loaded from database
    presets: [],
    
    // Currently active preset (full object)
    activePreset: null,
    
    // Loading state
    loading: false,
    
    // Initialization flag
    initialized: false
  }),

  getters: {
    /**
     * Get all presets
     */
    getAllPresets: (state) => state.presets,

    /**
     * Get the currently active preset
     */
    getActivePreset: (state) => state.activePreset,

    /**
     * Get active preset ID or null
     */
    getActivePresetId: (state) => state.activePreset?.id || null,

    /**
     * Get presets filtered by activity type
     */
    getPresetsByActivityType: (state) => (activityTypeId) => {
      return state.presets.filter(p => p.activityTypeId === activityTypeId);
    },

    /**
     * Get universal presets (applicable to all activities)
     */
    getUniversalPresets: (state) => {
      return state.presets.filter(p => p.activityTypeId === null);
    },

    /**
     * Get default preset for an activity type
     */
    getDefaultPresetForActivity: (state) => (activityTypeId) => {
      return state.presets.find(
        p => p.activityTypeId === activityTypeId && p.isDefault === true
      ) || null;
    },

    /**
     * Check if there's an active preset
     */
    hasActivePreset: (state) => state.activePreset !== null,

    /**
     * Get a specific geolocation setting from active preset
     */
    getActiveSetting: (state) => (settingName) => {
      return state.activePreset?.[settingName] ?? null;
    }
  },

  actions: {
    /**
     * Initialize presets store - load from database
     */
    async initPresets () {
      if (this.initialized) {
        appDebugLog.log('[SettingsPresets] Already initialized');
        return;
      }

      this.loading = true;
      
      try {
        // Load all presets
        this.presets = await GeoMoverDB.getAllPresets();
        
        // Load active preset
        this.activePreset = await GeoMoverDB.getActivePreset();
        
        this.initialized = true;
        appDebugLog.log('[SettingsPresets] Initialized:', {
          presetsCount: this.presets.length,
          activePreset: this.activePreset?.presetName || 'none'
        });
      }
      catch (error) {
        appDebugLog.error('[SettingsPresets] Error initializing:', error);
      }
      finally {
        this.loading = false;
      }
    },

    /**
     * Reload presets from database
     */
    async reloadPresets () {
      this.loading = true;
      
      try {
        this.presets = await GeoMoverDB.getAllPresets();
        this.activePreset = await GeoMoverDB.getActivePreset();
      }
      catch (error) {
        appDebugLog.error('[SettingsPresets] Error reloading:', error);
      }
      finally {
        this.loading = false;
      }
    },

    /**
     * Switch to a different preset
     * @param {number} presetId - The preset ID to activate
     */
    async switchPreset (presetId) {
      try {
        await GeoMoverDB.setActivePreset(presetId);
        
        // Update local state
        this.activePreset = this.presets.find(p => p.id === presetId) || null;
        
        // Update all presets' isActive state locally
        this.presets = this.presets.map(p => ({
          ...p,
          isActive: p.id === presetId
        }));
        
        appDebugLog.log('[SettingsPresets] Switched to preset:', this.activePreset?.presetName);
        return true;
      }
      catch (error) {
        appDebugLog.error('[SettingsPresets] Error switching preset:', error);
        return false;
      }
    },

    /**
     * Clear active preset (use "no preset" mode)
     */
    async clearActivePreset () {
      try {
        // Deactivate all presets
        for (const preset of this.presets) {
          if (preset.isActive) {
            preset.isActive = false;
            await GeoMoverDB.updateOne('settingsPresets', { isActive: false }, preset.id);
          }
        }
        
        this.activePreset = null;
        appDebugLog.log('[SettingsPresets] Cleared active preset');
        return true;
      }
      catch (error) {
        appDebugLog.error('[SettingsPresets] Error clearing active preset:', error);
        return false;
      }
    },

    /**
     * Create a new preset
     * @param {Object} presetData - The preset data
     * @returns {Promise<number|null>} The new preset ID or null on error
     */
    async createPreset (presetData) {
      try {
        const newId = await GeoMoverDB.addOne('settingsPresets', {
          ...presetData,
          createdAt: Date.now()
        });
        
        await this.reloadPresets();
        appDebugLog.log('[SettingsPresets] Created preset:', presetData.presetName, 'ID:', newId);
        return newId;
      }
      catch (error) {
        appDebugLog.error('[SettingsPresets] Error creating preset:', error);
        return null;
      }
    },

    /**
     * Update an existing preset
     * @param {number} presetId - The preset ID to update
     * @param {Object} updates - The fields to update
     */
    async updatePreset (presetId, updates) {
      try {
        await GeoMoverDB.updateOne('settingsPresets', updates, presetId);
        
        // Update local state
        const index = this.presets.findIndex(p => p.id === presetId);
        if (index !== -1) {
          this.presets[index] = { ...this.presets[index], ...updates };
        }
        
        // If updating active preset, update that too
        if (this.activePreset?.id === presetId) {
          this.activePreset = { ...this.activePreset, ...updates };
        }
        
        appDebugLog.log('[SettingsPresets] Updated preset:', presetId);
        return true;
      }
      catch (error) {
        appDebugLog.error('[SettingsPresets] Error updating preset:', error);
        return false;
      }
    },

    /**
     * Delete a preset
     * @param {number} presetId - The preset ID to delete
     */
    async deletePreset (presetId) {
      try {
        const preset = this.presets.find(p => p.id === presetId);
        
        // Don't allow deleting the last preset for an activity
        const activityPresets = this.presets.filter(
          p => p.activityTypeId === preset?.activityTypeId
        );
        if (activityPresets.length <= 1) {
          appDebugLog.warn('[SettingsPresets] Cannot delete last preset for activity');
          return false;
        }
        
        await GeoMoverDB.deleteOne('settingsPresets', presetId);
        
        // If deleting active preset, activate another one
        if (this.activePreset?.id === presetId) {
          const nextPreset = activityPresets.find(p => p.id !== presetId);
          if (nextPreset) {
            await this.switchPreset(nextPreset.id);
          }
          else {
            this.activePreset = null;
          }
        }
        
        // Update local state
        this.presets = this.presets.filter(p => p.id !== presetId);
        
        appDebugLog.log('[SettingsPresets] Deleted preset:', presetId);
        return true;
      }
      catch (error) {
        appDebugLog.error('[SettingsPresets] Error deleting preset:', error);
        return false;
      }
    },

    /**
     * Delete all presets for an activity type (cascade delete)
     * Called when an activity type is deleted
     * @param {number} activityTypeId - The activity type ID
     */
    async deletePresetsForActivityType (activityTypeId) {
      try {
        const deletedCount = await GeoMoverDB.deletePresetsByActivityType(activityTypeId);
        
        // Update local state
        this.presets = this.presets.filter(p => p.activityTypeId !== activityTypeId);
        
        // If active preset was deleted, clear it
        if (this.activePreset?.activityTypeId === activityTypeId) {
          this.activePreset = null;
        }
        
        appDebugLog.log('[SettingsPresets] Deleted', deletedCount, 'presets for activity:', activityTypeId);
        return deletedCount;
      }
      catch (error) {
        appDebugLog.error('[SettingsPresets] Error in cascade delete:', error);
        return 0;
      }
    },

    /**
     * Create a default preset for a new activity type
     * @param {number} activityTypeId - The activity type ID
     * @param {string} activityName - The activity name
     */
    async createDefaultPresetForActivity (activityTypeId, activityName) {
      try {
        const newId = await GeoMoverDB.createDefaultPreset(activityTypeId, activityName);
        await this.reloadPresets();
        
        appDebugLog.log('[SettingsPresets] Created default preset for:', activityName, 'ID:', newId);
        return newId;
      }
      catch (error) {
        appDebugLog.error('[SettingsPresets] Error creating default preset:', error);
        return null;
      }
    },

    /**
     * Update a single setting in the active preset
     * @param {string} settingName - The setting key
     * @param {*} value - The new value
     */
    async updateActiveSetting (settingName, value) {
      if (!this.activePreset) {
        appDebugLog.warn('[SettingsPresets] No active preset to update');
        return false;
      }

      return this.updatePreset(this.activePreset.id, { [settingName]: value });
    }
  }
});
